import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../app/hook";
import { showModal } from "../UI/modal/modal-slice";
import { AddressFormUI } from "../UI/address/MainForm";
import { Table } from "./Table";
import {
  checkCorporate,
  registerCorporateData,
} from "../UI/address/CorporateNumberSearch";
import { checkAddress, registerAddressData } from "../UI/address/AddressForm";
import {
  Box,
  Button,
  Form,
  ColumnLayout,
  SpaceBetween,
  Container,
  FormField,
  Header,
  Tiles,
} from "@cloudscape-design/components-themed";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import {
  HoldingsRegister,
  HoldingsUpdate,
  HoldingsUpdateInfo,
  CorporateNumberInfo,
  RegisterAddressInfo,
} from "../../features/api-interfaces";
import {
  useGetAuthQuery,
  usePostHoldingsRegisterMutation,
  usePutHoldingsUpdateMutation,
} from "../../features/api";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { USER_TYPE } from "../../configs/constants";

/*
ホールディングス案件登録画面
 */
export const HoldingsProjectForm = (props: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [postHoldingsRegister] = usePostHoldingsRegisterMutation();
  const [putHoldingsUpdate] = usePutHoldingsUpdateMutation();
  const { companyCode } = useParams();
  const { data: userInfo } = useGetAuthQuery();

  const isHdUser = useCanIShowHook(USER_TYPE.HD);

  // データをセット
  let holdingsProjectData: HoldingsUpdateInfo = props.holdingsProjectData;

  // 参照グループ種別
  const [referenceGroupTypeValue, setReferenceGroupTypeValue] = useState("1");

  // 登録画面か更新画面かのフラグ
  const isRegister = companyCode ? false : true;

  //戻るボタンの表示確認フラグ（入力フォームに何か入力された場合はtrue）
  const [isFormEntered, setIsFormEntered] = useState(false);

  // ホールディングス案件情報
  const [holdingsRegister, setHoldingsRegister] = useState({
    corporateNumber: "",
    name: "",
    nameKana: "",
    postalCode: "",
    address1: "",
    address1Kn: "",
    address2: "",
    address2Kn: "",
    prfCd: "",
    ctyCd: "",
    lrgCd: "",
    chrCd: "",
    phoneNumber: "",
    referenceGroupType: "",
  } as HoldingsRegister);

  // 所在情報
  const [address, setAddress] = useState({
    postalCode: "",
    address1: "",
    address1Kn: "",
    address2: "",
    address2Kn: "",
    prfCd: "",
    ctyCd: "",
    lrgCd: "",
    chrCd: "",
    phoneNumber: "",
  } as RegisterAddressInfo);

  //法人番号情報
  const [corporateData, setCorporateData] = useState({
    corporateNumber: "",
    companyName: "",
    companyNameKana: "",
  } as CorporateNumberInfo);

  // 参照グループ種別
  const referenceGroupTypeItems = [
    { value: "1", label: LABELS.UNIFIED_HOLDINGS },
    { value: "2", label: LABELS.SELECT_BY_COMPANY },
  ];

  useEffect(() => {
    // 更新の場合、DBに登録済みのデータを取得する
    if (holdingsProjectData !== undefined) {
      setHoldingsRegister({
        corporateNumber: holdingsProjectData.corporateNumber,
        name: holdingsProjectData.name,
        nameKana: holdingsProjectData.nameKana,
        postalCode: holdingsProjectData.postalCode,
        address1: holdingsProjectData.address1,
        address1Kn: holdingsProjectData.address1Kn,
        address2: holdingsProjectData.address2,
        address2Kn: holdingsProjectData.address2Kn,
        prfCd: holdingsProjectData.prfCd,
        ctyCd: holdingsProjectData.ctyCd,
        lrgCd: holdingsProjectData.lrgCd,
        chrCd: holdingsProjectData.chrCd,
        phoneNumber: holdingsProjectData.phoneNumber,
        referenceGroupType: holdingsProjectData.referenceGroupType,
      });
      setAddress({
        postalCode: holdingsProjectData.postalCode,
        address1: holdingsProjectData.address1,
        address1Kn: holdingsProjectData.address1Kn,
        address2: holdingsProjectData.address2,
        address2Kn: holdingsProjectData.address2Kn,
        prfCd: holdingsProjectData.prfCd,
        ctyCd: holdingsProjectData.ctyCd,
        lrgCd: holdingsProjectData.lrgCd,
        chrCd: holdingsProjectData.chrCd,
        phoneNumber: holdingsProjectData.phoneNumber,
      });
      setCorporateData({
        corporateNumber: holdingsProjectData.corporateNumber,
        companyName: holdingsProjectData.name,
        companyNameKana: holdingsProjectData.nameKana,
      });
    }
  }, [holdingsProjectData]);

  // 更新処理
  const holdingsProjectUpdate = () => {
    //バリエーションチェック
    const isCorporateValid = checkCorporate();
    const isAddressValid = checkAddress();

    if (!isCorporateValid || !isAddressValid) return;

    //入力した内容を詰める
    const holdingsProjectUpdateData = {
      companyCode: companyCode,
      corporateNumber: registerCorporateData.corporateNumber,
      name: registerCorporateData.companyName,
      nameKana: registerCorporateData.companyNameKana,
      postalCode: registerAddressData.postalCode,
      address1: registerAddressData.address1,
      address1Kn: registerAddressData.address1Kn,
      address2: registerAddressData.address2,
      address2Kn: registerAddressData.address2Kn,
      prfCd: registerAddressData.prfCd,
      ctyCd: registerAddressData.ctyCd,
      lrgCd: registerAddressData.lrgCd,
      chrCd: registerAddressData.chrCd,
      phoneNumber: registerAddressData.phoneNumber,
      referenceGroupType: holdingsProjectData.referenceGroupType,
    } as HoldingsUpdate;

    //更新処理呼び出し
    putHoldingsUpdate(holdingsProjectUpdateData).then((result: any) => {
      if (!("error" in result) && "error" !== result.data.status && !isHdUser) {
        navigate("/holdings");
      }
    });
  };

  // 更新ボタン押下イベント
  const onClickUpdateFormButton = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.UPDATE_CONFIRM,
        positiveFn: () => {
          holdingsProjectUpdate();
        },
      })
    );
  };

  // 登録処理
  const holdingsProjectRegister = () => {
    //バリエーションチェック
    const isCorporateValid = checkCorporate();
    const isAddressValid = checkAddress();

    if (!isCorporateValid || !isAddressValid) return;

    //入力した内容を詰める
    holdingsRegister.corporateNumber = registerCorporateData.corporateNumber;
    holdingsRegister.name = registerCorporateData.companyName;
    holdingsRegister.nameKana = registerCorporateData.companyNameKana;
    holdingsRegister.postalCode = registerAddressData.postalCode;
    holdingsRegister.address1 = registerAddressData.address1;
    holdingsRegister.address1Kn = registerAddressData.address1Kn;
    holdingsRegister.address2 = registerAddressData.address2;
    holdingsRegister.address2Kn = registerAddressData.address2Kn;
    holdingsRegister.prfCd = registerAddressData.prfCd;
    holdingsRegister.ctyCd = registerAddressData.ctyCd;
    holdingsRegister.lrgCd = registerAddressData.lrgCd;
    holdingsRegister.chrCd = registerAddressData.chrCd;
    holdingsRegister.phoneNumber = registerAddressData.phoneNumber;
    holdingsRegister.referenceGroupType = referenceGroupTypeValue;

    // 登録処理API呼び出し
    postHoldingsRegister(holdingsRegister).then((result: any) => {
      if (!("error" in result) && "error" !== result.data.status) {
        navigate(
          "/holdings-update/" +
            result.data.companyCode +
            "/" +
            registerCorporateData.companyName
        );
      }
    });
  };

  // 登録ボタン押下イベント
  const onClickRegisterFormButton = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
        positiveFn: () => {
          holdingsProjectRegister();
        },
      })
    );
  };

  //戻るボタン押下時のダイアログ
  const returnToList = () => {
    if (!isFormEntered) {
      navigate("/holdings");
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.PAGE_MOVE_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            navigate("/holdings");
          },
        })
      );
    }
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <form onSubmit={(e) => e.preventDefault()}>
        <SpaceBetween direction="vertical" size="l">
          <Form>
            {isRegister ? (
              <SpaceBetween size="l">
                <AddressFormUI
                  addressData={address}
                  corporateData={corporateData}
                  setIsFormEntered={setIsFormEntered}
                  isRegister={isRegister}
                  hasCreatedAt={holdingsProjectData?.updatedAt}
                  hasCreatedBy={holdingsProjectData?.updatedBy}
                  isUpdatable={true}
                />
                <Container
                  header={
                    <Header variant="h2">{LABELS.REFERENCE_GROUP_INFO}</Header>
                  }
                >
                  <FormField
                    label={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="strong">
                          {LABELS.REFERENCE_GROUP_TYPE}
                        </Box>
                        <Box variant="small" color="text-status-error">
                          {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                        </Box>
                      </SpaceBetween>
                    }
                  >
                    <Tiles
                      onChange={({ detail }) =>
                        setReferenceGroupTypeValue(detail.value)
                      }
                      items={referenceGroupTypeItems}
                      value={referenceGroupTypeValue}
                      columns={2}
                    ></Tiles>
                  </FormField>
                </Container>
              </SpaceBetween>
            ) : (
              <AddressFormUI
                addressData={address}
                corporateData={corporateData}
                setIsFormEntered={setIsFormEntered}
                isRegister={isRegister}
                hasCreatedAt={holdingsProjectData?.updatedAt}
                hasCreatedBy={holdingsProjectData?.updatedBy}
                isUpdatable={true}
              />
            )}
          </Form>
          <ColumnLayout columns={2}>
            {isRegister && (
              <Box float="left">
                <Button formAction="none" variant="link" onClick={returnToList}>
                  {COMMON_LABELS.BTNS.BACK}
                </Button>
              </Box>
            )}
            {isRegister && (
              <Box float="right">
                <Button onClick={onClickRegisterFormButton} variant="primary">
                  {COMMON_LABELS.BTNS.REGISTER}
                </Button>
              </Box>
            )}
          </ColumnLayout>
          {!isRegister && (
            <Box float="right">
              <Button onClick={onClickUpdateFormButton} variant="primary">
                {COMMON_LABELS.BTNS.UPDATE}
              </Button>
            </Box>
          )}
        </SpaceBetween>
      </form>
      {!isRegister && (
        <Table
          holdingsAffiliatedStoreCompanyData={
            props.holdingsAffiliatedStoreCompanyData
          }
          refetchHoldingsAffiliatedStoreCompanyData={
            props.refetchHoldingsAffiliatedStoreCompanyData
          }
          isFetching={props.isFetching}
        />
      )}

      {!isRegister && userInfo?.companyType !== USER_TYPE.HD && (
        <Box textAlign="left">
          <Button formAction="none" variant="link" onClick={returnToList}>
            {COMMON_LABELS.BTNS.BACK}
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
};
